import type { ReactElement } from "react";
import { useCallback, useState } from "react";

import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { OverlayScrollableContent } from "app/pages/sales-intelligence/common-components/filters-panel/components/ExpandedFiltersOverlay/OverlayScrollableContent";
import { FilterCategoryChipList } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterCategoryChipList/FilterCategoryChipList";
import { CategorizedFiltersGrid } from "app/pages/sales-intelligence/common-components/filters-panel/components/FiltersList/CategorizedFiltersGrid";
import FiltersListLoader from "app/pages/sales-intelligence/common-components/filters-panel/components/FiltersListLoader/FiltersListLoader";
import { FilterPanelItem } from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/FilterPanelItem/FilterPanelItem";
import { useCategorizedFiltersViewModel } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useCategorizedFiltersViewModel";
import { useFiltersManager } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useFiltersManager";
import { useLocalFilterListSearch } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useLocalFilterListSearch";
import { useLocalVisibleFilters } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useLocalVisibleFilters";
import type { FilterSearchConfigMap } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/filter-search-service/types";
import type {
    FilterCategory,
    IFiltersConfigCategoryItem,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/category";
import type {
    FilterInstanceType,
    IFilterInstance,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type { FiltersConfigStatus } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import type { LeadsSearchFilterInstance } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilterInstance";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";

import { StyledScoringFiltersPanelBleed } from "./styles";

type Props = {
    panelValue: Partial<LeadsSearchAllFiltersTypes>;
    onPanelValueUpdate: (next: Partial<LeadsSearchAllFiltersTypes>) => void;
    // Driven by the parent so the search input can live next to the step heading rather than in
    // the panel itself; matches the wizard mock where heading + search share one row.
    searchQuery: string;
    filtersList: readonly LeadsSearchFilterInstance[];
    filtersSearchConfigList: readonly FilterSearchConfigMap[];
    categoriesList: readonly IFiltersConfigCategoryItem[];
    status: FiltersConfigStatus;
};

/**
 * Leads + CRM filter panel embedded in the scoring rule wizard.
 * Filters config is owned by the wizard; this panel only renders the chips + grid for the
 * supplied list.
 */
export const ScoringFiltersPanel = ({
    panelValue,
    onPanelValueUpdate,
    searchQuery,
    filtersList,
    filtersSearchConfigList,
    categoriesList,
    status,
}: Props) => {
    const [selectedCategory, setSelectedCategory] = useState<FilterCategory>("all");
    const [expandedFilterKey, setExpandedFilterKey] = useState<string | null>(null);

    const isReady = status === "ready";

    const { searchResults, isSearchActive } = useLocalFilterListSearch({
        filtersConfig: filtersList,
        searchConfigMaps: filtersSearchConfigList,
        searchQuery,
    });

    const { filterList: visibleFilters, categoriesWithHiddenFilters } = useLocalVisibleFilters({
        filtersConfig: filtersList,
        selectedCategory,
        searchMatchedFilters: searchResults?.matchedFilters,
        isSearchActive,
    });

    const filtersManager = useFiltersManager<LeadsSearchAllFiltersTypes>({
        filtersValue: panelValue,
        filtersConfig: visibleFilters as unknown as readonly IFilterInstance<LeadsSearchAllFiltersTypes>[],
        onFiltersUpdate: onPanelValueUpdate,
    });

    const { permanentFilters, groups, categoriesWithCounts } = useCategorizedFiltersViewModel({
        visibleFilters: filtersManager.getAll(),
        fullFiltersConfig: filtersList as unknown as readonly IFilterInstance<LeadsSearchAllFiltersTypes>[],
        categories: categoriesList,
        categoriesWithHiddenFilters,
        isSearchActive,
    });

    const renderFilterCard = useCallback(
        (config: IFilterInstance<LeadsSearchAllFiltersTypes>): ReactElement => {
            const key = String(config.key);
            return (
                <FilterPanelItem<LeadsSearchAllFiltersTypes>
                    key={key}
                    isExpanded={expandedFilterKey === key}
                    filterConfig={config as unknown as FilterInstanceType<LeadsSearchAllFiltersTypes, unknown, unknown>}
                    filterDtoValue={filtersManager.getFilterDtoValue(config.key)}
                    onFilterUpdate={filtersManager.updateFilter}
                    onToggleExpand={() => setExpandedFilterKey((prev) => (prev === key ? null : key))}
                    isShowClearButton={filtersManager.isFilterDirty(config.key)}
                    isDisabled={filtersManager.isFilterDisabled(config.key)}
                    onClear={() => filtersManager.resetFilter(config.key)}
                />
            );
        },
        [expandedFilterKey, filtersManager],
    );

    return (
        <StyledScoringFiltersPanelBleed>
            {isReady ? (
                <Flex
                    alignItems="center"
                    flexWrap="wrap"
                    gap="8px"
                    flexShrink={0}
                    data-automation="scoring-filters-chips"
                >
                    <FilterCategoryChipList
                        categoryList={categoriesWithCounts}
                        selectedCategoryId={selectedCategory}
                        onClick={setSelectedCategory}
                    />
                </Flex>
            ) : null}

            <Stack
                flex="1"
                minHeight="0"
            >
                {isReady ? (
                    <OverlayScrollableContent
                        selectedCategory={selectedCategory}
                        scrollToTop={selectedCategory === "all"}
                    >
                        <Stack
                            gap="16px"
                            data-automation="scoring-filters-list"
                        >
                            <CategorizedFiltersGrid
                                permanentFilters={permanentFilters}
                                groups={groups}
                                renderFilterCard={renderFilterCard}
                            />
                        </Stack>
                    </OverlayScrollableContent>
                ) : (
                    <FiltersListLoader rowNumber={7} />
                )}
            </Stack>
        </StyledScoringFiltersPanelBleed>
    );
};
